import { gql } from "@apollo/client";
import { SIGNAL_FEED_ACTIVITY_ITEM_FIELDS, SIGNAL_FEED_INSTRUMENT_FIELDS, SIGNAL_FEED_PROFILE_FIELDS, } from "../graphql/signalFeedFragments.js";
export const SportMatchPredictionEventUpdatesSubscription = gql `
  subscription SportMatchPredictionEventUpdates($input: SportMatchPredictionEventUpdatesInput) {
    sportMatchPredictionEventUpdates(input: $input) {
      id
      standardizedInstrumentId
      leagueAbbreviation
      homeTeam
      awayTeam
      status
      live
      ended
      homeTeamScore
      awayTeamScore
      period
      elapsed
      lastUpdate
      finishedTimestamp
      turn
    }
  }
`;
/** Instrument price/volume/probability updates (token, perp, prediction) for home and list pages. */
export const InstrumentPriceUpdatesSubscription = gql `
  subscription InstrumentPriceUpdates($input: InstrumentPriceUpdatesInput) {
    instrumentPriceUpdates(input: $input) {
      ... on TokenPriceUpdate {
        __typename
        id
        standardizedInstrumentId
        currentPrice {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        currentMarketCap {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        currentTotalVolume {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        trades1h
        recentTrades {
          accountAddress
          amount
          symbol
          balanceImpact
          fiatValue
          timestamp
        }
        recentActivity {
          fiveMinutes {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
          oneHour {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
          fourHours {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
          oneDay {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
          sevenDays {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
          oneMonth {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
          sixMonths {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
          oneYear {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
          all {
            buys {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
            sells {
              numberOfTransactions
              totalVolume
              numberOfUniqueAccounts
            }
          }
        }
        chartData {
          fiveMinutes {
            timestamp
            open
            high
            low
            close
          }
          oneHour {
            timestamp
            open
            high
            low
            close
          }
          fourHours {
            timestamp
            open
            high
            low
            close
          }
          oneDay {
            timestamp
            open
            high
            low
            close
          }
          sevenDays {
            timestamp
            open
            high
            low
            close
          }
          oneMonth {
            timestamp
            open
            high
            low
            close
          }
          sixMonths {
            timestamp
            open
            high
            low
            close
          }
          oneYear {
            timestamp
            open
            high
            low
            close
          }
          all {
            timestamp
            open
            high
            low
            close
          }
        }
      }
      ... on PerpPriceUpdate {
        __typename
        id
        standardizedInstrumentId
        currentPrice {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        currentTotalVolume {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        trades1h
      }
      ... on PredictionEventPriceUpdate {
        __typename
        id
        standardizedInstrumentId
        volume {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        outcomes {
          externalTokenId
          price {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
        }
      }
    }
  }
`;
/** Newly discovered instruments (auto-registered from swap events, launchpad detection, etc.). */
export const NewInstrumentDiscoveredSubscription = gql `
  subscription NewInstrumentDiscovered($input: NewInstrumentDiscoveredInput) {
    newInstrumentDiscovered(input: $input) {
      ... on StandardizedFungibleToken {
        id
        standardizedInstrumentId
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        verified
        anchorToken {
          address
          platformId
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
        }
        metadata {
          socials {
            platform
            url
          }
          description
          createdAt
          launchpad
          lifecycleStage
          bondingCurveProgress
          holders
          txCount
          uniqueTraders
          confidenceScore
          devHoldingPercent
          sniperHoldingPercent
          top10HoldersPercent
          bundleHoldingPercent
          insiderHoldingPercent
          buyCount
          sellCount
        }
        currentPrice {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        currentMarketCap {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        currentTotalVolume {
          start {
            timestamp
            value
          }
          end {
            timestamp
            value
          }
          changeInPercentage
        }
      }
      ... on StandardizedPredictionEvent {
        id
        standardizedInstrumentId
        title
        imageUrl
        closesAt
        eventType
        isSeries
        volume {
          end {
            timestamp
            value
          }
          changeInPercentage
        }
        sportMetadata {
          isLive
          gameStatus
          sportType
          league
          scheduledStartTime
          homeTeamScore
          awayTeamScore
          period
          elapsed
          homeTeam {
            name
            abbreviation
            logo
            color
            record
          }
          awayTeam {
            name
            abbreviation
            logo
            color
            record
          }
          homeTeamWin {
            externalTokenId
            label
            color
            price {
              end {
                timestamp
                value
              }
            }
          }
          awayTeamWin {
            externalTokenId
            label
            color
            price {
              end {
                timestamp
                value
              }
            }
          }
          draw {
            externalTokenId
            label
            color
            price {
              end {
                timestamp
                value
              }
            }
          }
        }
        markets {
          question
          groupItemTitle
          sportsMarketType
          outcomes {
            externalTokenId
            label
            color
            price {
              end {
                timestamp
                value
              }
            }
          }
        }
      }
    }
  }
`;
/** Orderbook updates for perps and prediction markets (per-outcome for predictions). */
export const InstrumentOrderbookUpdatesSubscription = gql `
  subscription InstrumentOrderbookUpdates($input: InstrumentOrderbookUpdatesInput) {
    instrumentOrderbookUpdates(input: $input) {
      id
      standardizedInstrumentId
      orderBook {
        dataTimestamp
        bids {
          price
          size
          numOrders
        }
        asks {
          price
          size
          numOrders
        }
      }
      marketId
      outcomeTokenId
    }
  }
`;
export const InstrumentLifecycleTransitionSubscription = gql `
  subscription InstrumentLifecycleTransition {
    instrumentLifecycleTransition {
      standardizedInstrumentId
      fromStage
      toStage
      instrument {
        ... on StandardizedFungibleToken {
          id
          standardizedInstrumentId
          currency {
            asset {
              symbol
              name
            }
            decimals
            logoUrl
          }
          verified
          currentPrice {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentMarketCap {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          currentTotalVolume {
            start {
              timestamp
              value
            }
            end {
              timestamp
              value
            }
            changeInPercentage
          }
          anchorToken {
            currency {
              asset {
                name
                symbol
              }
              decimals
              logoUrl
            }
            address
            chainId
            platformId
          }
          metadata {
            description
            createdAt
            launchpad
            lifecycleStage
            bondingCurveProgress
            holders
            txCount
            uniqueTraders
            confidenceScore
            devHoldingPercent
            sniperHoldingPercent
            top10HoldersPercent
            bundleHoldingPercent
            insiderHoldingPercent
            buyCount
            sellCount
            socials {
              platform
              url
            }
          }
        }
      }
    }
  }
`;
export const InstrumentTradeUpdatesSubscription = gql `
  subscription InstrumentTradeUpdates($input: InstrumentTradeUpdatesInput!) {
    instrumentTradeUpdates(input: $input) {
      accountAddress
      amount
      symbol
      balanceImpact
      fiatValue
      timestamp
    }
  }
`;
/** Real-time perp trade updates from the Hyperliquid WebSocket feed. */
export const PerpTradeUpdatesSubscription = gql `
  subscription PerpTradeUpdates($input: InstrumentTradeUpdatesInput!) {
    perpTradeUpdates(input: $input) {
      venue
      symbol
      tradeId
      orderId
      side
      price {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        value
        amount
      }
      size {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        value
        amount
      }
      value {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        value
        amount
      }
      fee {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        value
        amount
      }
      closedPnl {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        value
        amount
      }
      positionBefore {
        currency {
          asset {
            symbol
            name
          }
          decimals
          logoUrl
        }
        value
        amount
      }
      isMaker
      timestamp
      leverage
    }
  }
`;
/** Real-time prediction comment updates from the Polymarket live-data WebSocket. */
export const PredictionCommentUpdatesSubscription = gql `
  subscription PredictionCommentUpdates($input: InstrumentTradeUpdatesInput!) {
    predictionCommentUpdates(input: $input) {
      id
      body
      username
      profileImage
      userAddress
      createdAt
      reactionCount
      parentCommentId
    }
  }
`;
/** Real-time prediction trade updates from the Polymarket activity WebSocket feed. */
export const PredictionTradeUpdatesSubscription = gql `
  subscription PredictionTradeUpdates($input: InstrumentTradeUpdatesInput!) {
    predictionTradeUpdates(input: $input) {
      timestamp
      outcomeTokenId
      conditionId
      outcomeName
      side
      size
      price
      volumeUsd
      traderAddress
      txHash
    }
  }
`;
/**
 * Real-time Signal feed — same `SignalsItem` shape as `listSignals` edges.
 */
export const SignalFeedUpdatesSubscription = gql `
  subscription SignalFeedUpdates($input: SignalFeedUpdatesInput!) {
    signalFeedUpdates(input: $input) {
      id
      instrumentType
      convictionMultiple
      profile {
        ...SignalFeedProfileFields
      }
      instrument {
        ...SignalFeedInstrumentFields
      }
      activityItem {
        ...SignalFeedActivityItemFields
      }
    }
  }
  ${SIGNAL_FEED_PROFILE_FIELDS}
  ${SIGNAL_FEED_INSTRUMENT_FIELDS}
  ${SIGNAL_FEED_ACTIVITY_ITEM_FIELDS}
`;
